import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Collapse,
  AlertTitle,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  GitHub as GitHubIcon,
  LinkedIn as LinkedInIcon,
  Code as GitLabIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contactInfoApi } from '../api/contactInfo';
import { ContactInfo, UpdateContactInfoDto } from '../types/contactInfo';

const socialMediaLinks = [
  { 
    name: 'GitHub', 
    label: 'GitHub', 
    icon: 'github', 
    placeholder: 'https://github.com/username', 
    field: 'github' as keyof UpdateContactInfoDto,
    description: 'Your GitHub profile URL'
  },
  { 
    name: 'LinkedIn', 
    label: 'LinkedIn', 
    icon: 'linkedin', 
    placeholder: 'https://linkedin.com/in/username', 
    field: 'linkedin' as keyof UpdateContactInfoDto,
    description: 'Your LinkedIn profile URL'
  },
  { 
    name: 'GitLab', 
    label: 'GitLab', 
    icon: 'gitlab', 
    placeholder: 'https://gitlab.com/username', 
    field: 'gitlab' as keyof UpdateContactInfoDto,
    description: 'Your GitLab profile URL'
  },
];

const getSocialIcon = (icon: string) => {
  switch (icon) {
    case 'github': return <GitHubIcon />;
    case 'linkedin': return <LinkedInIcon />;
    case 'gitlab': return <GitLabIcon />;
    default: return <GitHubIcon />;
  }
};

interface FormErrors {
  email?: string;
  phone?: string;
  address?: {
    en?: string;
    ru?: string;
  };
  github?: string;
  linkedin?: string;
  gitlab?: string;
}

export default function ContactInfoPage() {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<UpdateContactInfoDto>({});
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [expandedSections, setExpandedSections] = useState({
    contact: true,
    social: true,
  });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch main contact info
  const { data: contactInfo, isLoading, error, refetch } = useQuery<ContactInfo>({
    queryKey: ['contactInfo'],
    queryFn: contactInfoApi.getMain,
    retry: 3,
    retryDelay: 1000,
  });

  useEffect(() => {
    if (contactInfo) {
      setFormData({
        email: contactInfo.email || '',
        phone: contactInfo.phone || '',
        address: contactInfo.address || { en: '', ru: '' },
        github: contactInfo.github || '',
        linkedin: contactInfo.linkedin || '',
        gitlab: contactInfo.gitlab || '',
        isActive: contactInfo.isActive,
      });
      setFormErrors({});
    }
  }, [contactInfo]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: contactInfoApi.updateMain,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contactInfo'] });
      setSnackbar({ 
        open: true, 
        message: 'Contact information updated successfully!', 
        severity: 'success' 
      });
      setFormErrors({});
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error updating contact information: ${error instanceof Error ? error.message : 'Unknown error'}`,
        severity: 'error'
      });
    },
  });

  // Initialize mutation
  const initializeMutation = useMutation({
    mutationFn: contactInfoApi.initialize,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contactInfo'] });
      setSnackbar({ 
        open: true, 
        message: 'Contact information initialized successfully!', 
        severity: 'success' 
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error initializing contact information: ${error instanceof Error ? error.message : 'Unknown error'}`,
        severity: 'error'
      });
    },
  });

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    // Email validation
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Phone validation (optional but if provided, validate format)
    if (formData.phone && !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      errors.phone = 'Please enter a valid phone number';
    }

    // URL validation for social media links
    const urlRegex = /^https?:\/\/.+/;
    if (formData.github && !urlRegex.test(formData.github)) {
      errors.github = 'Please enter a valid URL starting with http:// or https://';
    }
    if (formData.linkedin && !urlRegex.test(formData.linkedin)) {
      errors.linkedin = 'Please enter a valid URL starting with http:// or https://';
    }
    if (formData.gitlab && !urlRegex.test(formData.gitlab)) {
      errors.gitlab = 'Please enter a valid URL starting with http:// or https://';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field: keyof UpdateContactInfoDto, value: string | boolean | { en: string; ru: string }) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (formErrors[field as keyof FormErrors]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Please fix the errors in the form before submitting.',
        severity: 'error'
      });
      return;
    }

    updateMutation.mutate(formData);
  };

  const handleInitialize = () => {
    if (window.confirm('This will reset the contact information to default values. Are you sure?')) {
      initializeMutation.mutate();
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const toggleSection = (section: 'contact' | 'social') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const hasChanges = () => {
    if (!contactInfo) return false;
    return (
      formData.email !== contactInfo.email ||
      formData.phone !== contactInfo.phone ||
      formData.address !== contactInfo.address ||
      formData.github !== contactInfo.github ||
      formData.linkedin !== contactInfo.linkedin ||
      formData.gitlab !== contactInfo.gitlab ||
      formData.isActive !== contactInfo.isActive
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', flexDirection: 'column' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
          Loading contact information...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        sx={{ mb: 2 }}
        action={
          <Button color="inherit" size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        <AlertTitle>Error loading contact information</AlertTitle>
        {error instanceof Error ? error.message : 'Unknown error occurred'}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Contact Information Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your contact details and social media links
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            variant="outlined"
            disabled={isLoading}
          >
            Refresh
          </Button>
          <Button
            onClick={handleInitialize}
            variant="outlined"
            disabled={initializeMutation.isPending}
            color="warning"
          >
            Initialize
          </Button>
        </Box>
      </Box>

      {/* Status Card */}
      {contactInfo && (
        <Card sx={{ mb: 3, bgcolor: 'background.default' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                icon={contactInfo.isActive ? <CheckCircleIcon /> : <ErrorIcon />}
                label={contactInfo.isActive ? 'Active' : 'Inactive'}
                color={contactInfo.isActive ? 'success' : 'error'}
                variant="outlined"
              />
              <Typography variant="body2" color="text.secondary">
                Last updated: {new Date(contactInfo.updatedAt).toLocaleString()}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      <form onSubmit={handleSubmit}>
        {/* Contact Information */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">
                Contact Information
              </Typography>
              <IconButton onClick={() => toggleSection('contact')}>
                {expandedSections.contact ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            
            <Collapse in={expandedSections.contact}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  label="Email"
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  fullWidth
                  required
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                        <EmailIcon />
                      </Box>
                    ),
                  }}
                />
                
                <TextField
                  label="Phone"
                  value={formData.phone || ''}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  fullWidth
                  error={!!formErrors.phone}
                  helperText={formErrors.phone || 'Optional: Include country code'}
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                        <PhoneIcon />
                      </Box>
                    ),
                  }}
                />
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Address
                  </Typography>
                  
                  <TextField
                    label="Address (English)"
                    value={formData.address?.en || ''}
                    onChange={(e) => handleInputChange('address', { 
                      en: e.target.value,
                      ru: formData.address?.ru || ''
                    })}
                    fullWidth
                    multiline
                    rows={3}
                    error={!!formErrors.address?.en}
                    helperText={formErrors.address?.en || 'Optional: Your business address in English'}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'flex-start', mt: 1 }}>
                          <LocationIcon />
                        </Box>
                      ),
                    }}
                  />

                  <TextField
                    label="Address (Russian)"
                    value={formData.address?.ru || ''}
                    onChange={(e) => handleInputChange('address', {
                      en: formData.address?.en || '',
                      ru: e.target.value
                    })}
                    fullWidth
                    multiline
                    rows={3}
                    error={!!formErrors.address?.ru}
                    helperText={formErrors.address?.ru || 'Optional: Your business address in Russian'}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'flex-start', mt: 1 }}>
                          <LocationIcon />
                        </Box>
                      ),
                    }}
                  />
                </Box>

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive ?? true}
                      onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    />
                  }
                  label="Active"
                />
              </Box>
            </Collapse>
          </CardContent>
        </Card>

        {/* Social Media Links */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">
                Social Media Links
              </Typography>
              <IconButton onClick={() => toggleSection('social')}>
                {expandedSections.social ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            
            <Collapse in={expandedSections.social}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {socialMediaLinks.map((link) => (
                  <Box key={link.field}>
                    <TextField
                      label={link.label}
                      type="url"
                      value={link.field === 'address' ? '' : (formData[link.field] as string) || ''}
                      onChange={(e) => handleInputChange(link.field, e.target.value)}
                      fullWidth
                      placeholder={link.placeholder}
                      error={!!(formErrors[link.field as keyof FormErrors] as string)}
                      helperText={(formErrors[link.field as keyof FormErrors] as string) || link.description}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                            {getSocialIcon(link.icon)}
                          </Box>
                        ),
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Collapse>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, gap: 2 }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            startIcon={<SaveIcon />}
            disabled={updateMutation.isPending || !hasChanges()}
            sx={{ minWidth: '200px' }}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
          
          {hasChanges() && (
            <Chip
              icon={<InfoIcon />}
              label="Unsaved changes"
              color="warning"
              variant="outlined"
            />
          )}
        </Box>
      </form>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          elevation={6}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
} 